import { useToast } from 'hooks';
import React, { useCallback } from 'react';
import {
	AccessKeyExpiration,
	AccessKeyType,
	IGenerateAccessKey,
	IGeneratedAccessKey
} from 'types/accessKeys';
import { IActionDialogProps } from 'types/common';
import { ToastSeverity } from 'types/toast';
import { useCopyToClipboard } from 'usehooks-ts';
import AccessKeyCopyDialog from './AccessKeyCopyDialog';
import AccessKeyGenerateDialog from './AccessKeyGenerateDialog';

const AccessKeyGenerationDialog: React.FC<
	IActionDialogProps & {
		defaultExpiration: AccessKeyExpiration;
		generateKey: (key: IGenerateAccessKey) => Promise<IGeneratedAccessKey>;
		translationPrefix: `${AccessKeyType}`;
		showTenantMapping?: boolean;
		showPermittedProjects?: boolean;
	}
> = ({ onCreate, onCancel, ...props }) => {
	const [keyGenerationOpen, setKeyGenerationOpen] = React.useState(true);
	const [generatedKey, setGeneratedKey] = React.useState('');

	const [, copyFn] = useCopyToClipboard();

	const { addToast } = useToast();

	const onComplete = useCallback(async () => {
		const copied = await copyFn(generatedKey);
		if (copied) {
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('toast.value.copied.to.clipboard')
			});
		}
		onCreate();
	}, [copyFn, addToast, generatedKey, onCreate]);

	return keyGenerationOpen ? (
		<AccessKeyGenerateDialog
			onCancel={onCancel}
			onSubmit={(key: string) => {
				setKeyGenerationOpen(false);
				setGeneratedKey(key);
			}}
			{...props}
		/>
	) : (
		<AccessKeyCopyDialog
			generatedKey={generatedKey}
			onComplete={onComplete}
			{...props}
		/>
	);
};

AccessKeyGenerationDialog.defaultProps = {
	showTenantMapping: false,
	showPermittedProjects: false
};

export default AccessKeyGenerationDialog;
